import axios from 'axios';
import { createSelector } from 'reselect';

import {
    CapabilityTypes,
    FavouriteChannelsListMetadataItem,
    PIN_STATUS,
    PIN_TYPES,
    ProfileParentalRating,
    RequestErrorData,
} from 'store/user/reducer.types';

import { ReduxStore } from '../types';
import { isCapabilityValid, requestFailure } from '../utils';

export function selectUser(store: ReduxStore) {
    return store.user;
}

export function selectLanguage(store: ReduxStore) {
    return store.user.language;
}

export function selectIfUserIsGuest(store: ReduxStore) {
    return store.user.isGuest;
}

export function selectIfUnauthorizedLoginError(store: ReduxStore) {
    const { error } = store.user.loginRequest;

    return error && axios.isAxiosError(error) && error.response?.status === 401;
}

export function selectLoginError(store: ReduxStore) {
    return store.user.loginRequest.error;
}

export function selectIfLoginRequestFailure(store: ReduxStore) {
    return requestFailure(store.user.loginRequest.status);
}

export function selectUserAuthData(store: ReduxStore) {
    return store.user.auth;
}

export function selectAccountId(store: ReduxStore) {
    return store.user.auth?.accountId;
}

export function selectRefreshTokenError(store: ReduxStore) {
    return store.user.refreshTokenError;
}

export function selectClientId(store: ReduxStore) {
    return store.user.clientId;
}

export function selectAccessToken(store: ReduxStore) {
    return store.user.auth?.access_token;
}

export function selectEntitlements(store: ReduxStore) {
    return store.user.entitlementsRequest.data || [];
}

export function selectSharedConsentPublicId(store: ReduxStore) {
    return store.user.sharedConsentPublicId;
}

export const selectTimeshiftCapability = createSelector(
    selectEntitlements,
    (entitlements) =>
        entitlements.find(
            ({ capabilityType }) =>
                capabilityType === CapabilityTypes.Timeshift,
        ),
);

export const selectStartoverCapability = createSelector(
    selectEntitlements,
    (entitlements) =>
        entitlements.find(
            ({ capabilityType }) => capabilityType === CapabilityTypes.SO,
        ),
);

export const selectCatchupCapability = createSelector(
    selectEntitlements,
    (entitlements) =>
        entitlements.find(
            ({ capabilityType }) => capabilityType === CapabilityTypes.CU,
        ),
);

export const selectPurchaseCapability = createSelector(
    selectEntitlements,
    (entitlements) =>
        entitlements.find(
            ({ capabilityType }) => capabilityType === CapabilityTypes.Purchase,
        ),
);

export function selectEntitlementsRequestStatus(store: ReduxStore) {
    return store.user.entitlementsRequest.status;
}

export const selectNPVRCapability = createSelector(
    selectEntitlements,
    (entitlements) =>
        entitlements?.find(
            ({ capabilityType }) => capabilityType === CapabilityTypes.NPVR,
        ),
);

export function selectParentalControlRating(store: ReduxStore) {
    return store.user.auth?.parentalControl?.ratingThreshold?.HCRS;
}

export const getModalVisible = createSelector(selectUser, (user) => {
    return user.modalVisible;
});

function getPinsStatusRequest(store: ReduxStore) {
    return store.user.pinsStatusRequest;
}

export const getPinsStatusRequestStatus = createSelector(
    getPinsStatusRequest,
    (request) => request.status,
);

export const getPinsStatusRequestErrorStatus = createSelector(
    getPinsStatusRequest,
    (request) => request.error?.response?.status,
);

export const getPinsStatusRequestErrorCode = createSelector(
    getPinsStatusRequest,
    (request) => (request.error?.response?.data as RequestErrorData)?.errorCode,
);

export const getHaveToShowPinSetup = createSelector(
    getPinsStatusRequest,
    (request) =>
        request.data?.find(
            (item) =>
                item.pinType === PIN_TYPES.AccessPin &&
                item.status === PIN_STATUS.UNSET,
        ),
);

export const selectAccessPinLockedTime = createSelector(
    getPinsStatusRequest,
    (pins) => {
        const accessPinStatus = pins.data.find(
            ({ pinType }) => pinType === PIN_TYPES.AccessPin,
        );

        return accessPinStatus?.lockTimeRemaining;
    },
);

export const selectFavouriteChannelsList = (store: ReduxStore) =>
    store.user.favouriteChannelsListRequest.data[0];

export const selectActiveFavouriteChannelsList = createSelector(
    selectFavouriteChannelsList,
    (favouriteChannelsList) => {
        if (
            Array.isArray(favouriteChannelsList?.tags) &&
            favouriteChannelsList?.tags.includes('active')
        ) {
            return favouriteChannelsList;
        }

        return undefined;
    },
);

export const selectShouldHideUnsubscribedChannels = createSelector(
    [selectFavouriteChannelsList],
    (favouriteChannelsList) =>
        favouriteChannelsList?.tags?.includes('unsubscribed_hidden') || false,
);

export const selectFavouriteChannelsRequestError = (store: ReduxStore) =>
    store.user.favouriteChannelsListRequest.error?.code;

function getProfilesListRequest(store: ReduxStore) {
    return store.user.profilesListRequest;
}

export const selectProfilesList = createSelector(
    getProfilesListRequest,
    ({ data }) => data,
);

export const selectIfKidsProfileActive = createSelector(
    selectActiveUserProfile,
    getProfilesListRequest,
    (profileId, { data }) =>
        !!data.find(({ _id }) => _id === profileId)?.childProfile,
);

export function selectActiveUserProfile(store: ReduxStore): string | undefined {
    return store.user.selectedProfile;
}

export function selectProfileListErrorCode(store: ReduxStore) {
    return (
        store.user.profilesListRequest.error?.response?.data as RequestErrorData
    )?.errorCode;
}

export function selectProfileListErrorStatus(store: ReduxStore) {
    return store.user.profilesListRequest.error?.response?.status;
}

export function selectChangeProfileErrorCode(store: ReduxStore) {
    return (
        store.user.changeUserProfileRequest.error?.response
            ?.data as RequestErrorData
    )?.errorCode;
}

export function selectChangeProfileErrorStatus(store: ReduxStore) {
    return store.user.changeUserProfileRequest.error?.response?.status;
}

export function selectAddProfileErrorCode(store: ReduxStore) {
    return (
        store.user.addProfileRequest.error?.response?.data as RequestErrorData
    )?.errorCode;
}

export function selectAddProfileErrorStatus(store: ReduxStore) {
    return store.user.addProfileRequest.error?.response?.status;
}

export function selectProfileParentalRating(
    store: ReduxStore,
): ProfileParentalRating | null {
    return store.user.profileParentalRating;
}

export function selectAddProfileRequestStatus(store: ReduxStore) {
    return store.user.addProfileRequest.status;
}

export function selectEditProfileStatus(store: ReduxStore) {
    return store.user.editProfileRequest.status;
}

export function selectEditProfileErrorCode(store: ReduxStore) {
    return (
        store.user.editProfileRequest.error?.response?.data as RequestErrorData
    )?.errorCode;
}

export function selectEditProfileErrorStatus(store: ReduxStore) {
    return store.user.editProfileRequest.error?.response?.status;
}

export function selectDeleteProfileStatus(store: ReduxStore) {
    return store.user.deleteProfileRequest.status;
}

export function selectDeleteProfileErrorCode(store: ReduxStore) {
    return (
        store.user.deleteProfileRequest.error?.response
            ?.data as RequestErrorData
    )?.errorCode;
}

export function selectDeleteProfileErrorStatus(store: ReduxStore) {
    return store.user.deleteProfileRequest.error?.response?.status;
}

export function selectProfileAvatar(store: ReduxStore): string | null {
    return store.user.profileAvatar;
}

export function selectCurrentProfileAvatar(
    store: ReduxStore,
): string | undefined {
    return store.user.currentProfileAvatar;
}

export function selectOTTLastConnectionTime(store: ReduxStore) {
    return store.user.OTTLastConnectionTime;
}

export function selectLastProvisionDate(store: ReduxStore) {
    return store.user.lastProvisionDate;
}

export function selectSuspension(store: ReduxStore) {
    return store.user.suspension;
}

export const selectValidSubscriptions = createSelector(
    selectEntitlements,
    (entitlements) =>
        entitlements.filter(
            (maybeSubscription) =>
                maybeSubscription.productType === 'SUBSCRIPTION' &&
                isCapabilityValid(maybeSubscription),
        ),
);

export const selectIfCollectionSuspensionActive = createSelector(
    selectValidSubscriptions,
    (subscriptions) =>
        subscriptions.length === 1 &&
        subscriptions[0].productId === CapabilityTypes.MUST_CARRY &&
        isCapabilityValid(subscriptions[0]),
);

export const selectIsNonInteractiveUI = createSelector(
    selectEntitlements,
    (entitlements) => {
        const nonInteractiveEntitlement = entitlements.find(
            (entitlement) =>
                entitlement.capabilityType === CapabilityTypes.NON_INTERACTIVE,
        );

        return isCapabilityValid(nonInteractiveEntitlement);
    },
);

export function selectCashedPins(store: ReduxStore) {
    return store.user.cashedPinByParentalRating;
}

export function selectLineupChanged(store: ReduxStore) {
    return store.user.isLineupChanged;
}

export function selectShowLineupModal(store: ReduxStore) {
    return store.user.shouldLineupModalShow;
}

export function selectConnectionType(store: ReduxStore) {
    return store.user.connectionType;
}
